import type { LanguagePack } from '../types';

export const enPack: LanguagePack<'en'> = {
  code: 'en',
  name: { native: 'English', english: 'English' },
  script: 'latin',
  dir: 'ltr',
  locales: { date: 'en-GB', number: 'en-GB' },

  detection: {
    scriptRanges: ['A-Za-z'],
    sttCodes: ['eng', 'en'],
    explicitSwitchPhrases: [
      /^(?:english|انجليزي|إنجليزي|انگریزی)$/iu,
      /\b(?:speak|reply|talk|write|answer)\s+(?:to me\s+)?(?:in\s+)?english\b/i,
      /\benglish\s+(?:please|pls)\b/i,
      /(?:تكلم|كلمني|تحدث|اكتب|رد)\s*(?:معي|معاي)?\s*(?:بال)?(?:انجليزي|إنجليزي|انقليزي)/u,
    ],
    greetingWords: ['hello', 'hi', 'hey', 'good morning', 'good evening', 'good afternoon'],
  },

  voice: {
    styleNote: [
      'Simple, friendly English for readers who often speak it as a second or third language (Arab, Pakistani, Indian, Filipino drivers).',
      'Short sentences and common words. No idioms, slang, or office phrases (kindly, revert, do the needful, please be informed).',
      'Warm but brief: "Sure", "Done", "Got it" read better than long apologies.',
      'Saudi document names stay recognisable: iqama, istimara (vehicle registration), waybill.',
      'Western digits. Plates and numbers exactly as on the card.',
      'One ask per message, at most one emoji.',
    ].join('\n'),
    examples: [
      {
        scenario: 'greeting',
        turns: [
          { role: 'user', text: 'hello' },
          {
            role: 'agent',
            text: 'Hi, welcome 👋 This is Wathiqa Plus. I issue your government transport waybill right here on WhatsApp. Are you a transport company, a broker, or a driver for a company?',
          },
        ],
        note: 'First reply on the line: who we are in one line, then the one open question.',
      },
      {
        scenario: 'returning_user',
        turns: [
          { role: 'user', text: 'hi' },
          { role: 'agent', text: 'Hi again! Where to today?' },
        ],
        note: 'Account and driver on file: no intro, straight to the lane.',
      },
      {
        scenario: 'cards_photo',
        turns: [
          { role: 'user', text: '[photos: iqama + istimara]' },
          {
            role: 'agent',
            text: 'Hi, this is Wathiqa Plus 👋 I read the iqama and istimara for Mohammed Khan. Are you his company, or are you the driver?',
          },
        ],
        note: 'Cards never decide the account type — ask it once, keep what was read.',
      },
      {
        scenario: 'account_type',
        turns: [
          { role: 'user', text: 'we are a transport company' },
          { role: 'agent', text: 'Great. What is the company name? I will set up your account.' },
        ],
      },
      {
        scenario: 'ask_identity',
        turns: [
          { role: 'user', text: 'i need waybill from riyadh to dammam tomorrow' },
          { role: 'agent', text: 'Sure. Send me a photo of your iqama and istimara, and I will do the rest.' },
        ],
        note: 'Lane and date already given — never ask for them again.',
      },
      {
        scenario: 'waybill_request',
        turns: [
          { role: 'user', text: 'jeddah riyadh' },
          { role: 'agent', text: '[stages the waybill — the card is the reply, no words]' },
        ],
      },
      {
        scenario: 'waybill_issued',
        turns: [{ role: 'agent', text: 'Done ✅ Your waybill number is 743198. Safe trip!' }],
      },
      {
        scenario: 'waybill_edit',
        turns: [
          { role: 'user', text: 'change the receiver to Al Noor Trading' },
          { role: 'agent', text: '[stages the edit — the card is the reply]' },
        ],
      },
      {
        scenario: 'price_question',
        turns: [
          { role: 'user', text: 'how much?' },
          { role: 'agent', text: 'Your first waybill is free, then each one is {price}. Shall I make one now?' },
        ],
        note: '{price} is copied from get_subscription_options — never from memory.',
      },
      {
        scenario: 'thanks_closer',
        turns: [
          { role: 'user', text: 'ok thanks' },
          { role: 'agent', text: '[react 👍]' },
        ],
        note: 'The task is done; a reaction is the whole answer.',
      },
      {
        scenario: 'sticker',
        turns: [
          { role: 'user', text: '[sticker: a truck waving goodbye]' },
          { role: 'agent', text: '[react 👋]' },
        ],
      },
      {
        scenario: 'error',
        turns: [
          {
            role: 'agent',
            text: 'Bayan did not accept the waybill: the plate does not match the istimara. Send me a photo of the istimara 📷 or type the right plate, and I will issue it again.',
          },
        ],
        note: 'Say what failed in plain words and the one thing that fixes it.',
      },
    ],
    glossary: {
      waybill: 'waybill',
      national_id: 'national ID',
      iqama: 'iqama',
      vehicle_registration: 'istimara (vehicle registration)',
      driver_license: 'driving licence',
      plate: 'plate',
      trip: 'trip',
    },
    reviewStatus: 'draft',
  },
};
